/**
 * Platform-neutral Connect Dog Card sharing (Slice 2.12E).
 * Web/PWA uses navigator.share with copy fallback.
 * Native Capacitor Share adapter is documented for Phase 2.14 — not wired here.
 */

export type ConnectDogCardSharePayload = {
  title: string
  text: string
  url: string
}

export type ConnectShareAdapterResult =
  | { outcome: 'shared' }
  | { outcome: 'cancelled' }
  | { outcome: 'unavailable' }
  | { outcome: 'failed'; message: string }

export type ConnectCopyLinkResult =
  | { outcome: 'copied' }
  | { outcome: 'manual_required'; url: string }
  | { outcome: 'failed'; message: string }

export function buildConnectDogCardShareMessage(dogName: string): string {
  const trimmed = dogName.trim() || 'your dog'
  return `Connect your dog with ${trimmed} on Wagamour.`
}

export function buildConnectDogCardSharePayload(
  dogName: string,
  dogCardUrl: string
): ConnectDogCardSharePayload {
  return {
    title: 'Connect our dogs',
    text: buildConnectDogCardShareMessage(dogName),
    url: dogCardUrl,
  }
}

export function isWebShareSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

export async function shareUrlPayload(
  payload: ConnectDogCardSharePayload
): Promise<ConnectShareAdapterResult> {
  if (!isWebShareSupported()) {
    return { outcome: 'unavailable' }
  }

  try {
    await navigator.share({
      title: payload.title,
      text: payload.text,
      url: payload.url,
    })
    return { outcome: 'shared' }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return { outcome: 'cancelled' }
    }
    return {
      outcome: 'failed',
      message: 'Sharing is unavailable right now.',
    }
  }
}

export async function shareConnectDogCard(
  payload: ConnectDogCardSharePayload
): Promise<ConnectShareAdapterResult> {
  return shareUrlPayload(payload)
}

async function copyTextWithFallback(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to execCommand
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}

export async function copyConnectDogCardUrl(url: string): Promise<ConnectCopyLinkResult> {
  const copied = await copyTextWithFallback(url)
  if (copied) {
    return { outcome: 'copied' }
  }
  return { outcome: 'manual_required', url }
}

export function formatConnectShareExpiry(expiresAtIso: string | null | undefined): string | null {
  if (!expiresAtIso?.trim()) return null
  const date = new Date(expiresAtIso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Production native candidate — @capacitor/share is not installed in this repo.
 * Phase 2.14 should implement this adapter without changing the sheet contract.
 */
export type NativeConnectShareAdapter = {
  share: (payload: ConnectDogCardSharePayload) => Promise<ConnectShareAdapterResult>
}

export const NATIVE_CONNECT_SHARE_ADAPTER_NOTE =
  'Capacitor Share is not configured in Slice 2.12E. Native apps should use a narrow adapter implementing NativeConnectShareAdapter.'
